import { FC, useEffect, useMemo, useCallback } from 'react'
import { PlanAddOn, useBilling } from '~/features/usage/useBilling'
import { removePlansFromProductMap } from '~/features/billing/helpers'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { MinusCircle, PlusCircle, Tag } from 'lucide-react'
import { ProductIcon } from '~/features/billing/componentsV2'
import { Badge } from '~/elements'

type AddonData = {
  id: string
  name: string
  price: number
  discount?: number
  description: string | null
  quotaName: string | undefined
  currentQuantity: number
  nextQuantity?: number
}

type Props = {
  workspaceId: string
  setHasPendingAddonChanges: (hasPendingAddonChanges: boolean) => void
  draftAddons: PlanAddOn[]
  setDraftAddons: (draftAddons: PlanAddOn[]) => void
}

const useAddonData = (workspaceId: string) => {
  const { currentMonthAddOns, nextMonthAddOns, initialProductQuantityMap, promoCode } = useBilling(workspaceId)

  const addOnsInitialProductMap = removePlansFromProductMap(initialProductQuantityMap)

  const addons = useMemo(() => {
    return Object.entries(addOnsInitialProductMap).map(([productId, { productDetails }]) => {
      const priceCents = productDetails.price.unit_amount ?? 0
      const discountCents = promoCode?.coupon.appliesTo.includes(productId)
        ? (priceCents * promoCode.coupon.percentOff) / 100
        : undefined

      return {
        id: productId,
        name: productDetails.name.replace(/Add-on - /i, ''),
        price: priceCents / 100,
        discount: discountCents ? discountCents / 100 : undefined,
        description: productDetails.description,
        quotaName: productDetails.metadata.quotaName,
        currentQuantity: currentMonthAddOns.find((addon) => addon.productId === productId)?.quantity || 0,
        nextQuantity: nextMonthAddOns.find((addon) => addon.productId === productId)?.quantity,
      }
    })
  }, [addOnsInitialProductMap, currentMonthAddOns, nextMonthAddOns, promoCode])

  return { addons, addOnsInitialProductMap }
}

export const AddOns: FC<Props> = ({ workspaceId, setHasPendingAddonChanges, draftAddons, setDraftAddons }) => {
  const { currentMonthAddOns, nextMonthAddOns } = useBilling(workspaceId)
  const { addons, addOnsInitialProductMap } = useAddonData(workspaceId)

  useEffect(() => {
    setDraftAddons(nextMonthAddOns)
  }, [nextMonthAddOns, setDraftAddons])

  useEffect(() => {
    if (draftAddons.length === 0) {
      setHasPendingAddonChanges(false)
      return
    }

    const hasChanges = draftAddons.some((addon) => {
      const nextMonthQuantity = nextMonthAddOns.find((n) => n.productId === addon.productId)?.quantity || 0
      return addon.quantity !== nextMonthQuantity
    })

    setHasPendingAddonChanges(hasChanges)
  }, [draftAddons, nextMonthAddOns, setHasPendingAddonChanges])

  const handleUpdateAddon = useCallback(
    (addonId: string, delta: number) => {
      const existingDraftAddon = draftAddons.find((addon) => addon.productId === addonId)
      const currentQuantity = existingDraftAddon?.quantity || 0
      const newQuantity = Math.max(0, currentQuantity + delta)

      let updatedDraftAddons: PlanAddOn[]

      if (existingDraftAddon) {
        updatedDraftAddons = draftAddons.map((addon) =>
          addon.productId === addonId ? { ...addon, quantity: newQuantity } : addon
        )
      } else {
        const newAddon: PlanAddOn = {
          name: addOnsInitialProductMap[addonId]?.productDetails?.name || '',
          productId: addonId,
          quantity: newQuantity,
        }
        updatedDraftAddons = [...draftAddons, newAddon]
      }

      setDraftAddons(updatedDraftAddons)
    },
    [draftAddons, addOnsInitialProductMap, setDraftAddons]
  )

  const isDecreaseDisabled = useCallback(
    (addonId: string) => {
      const draftAddon = draftAddons.find((addon) => addon.productId === addonId) || addOnsInitialProductMap[addonId]
      return !!(draftAddon && draftAddon.quantity === 0)
    },
    [draftAddons, addOnsInitialProductMap]
  )

  const renderChangeStatus = useCallback(
    (addonId: string) => (
      <AddonChangeStatus addonId={addonId} draftAddons={draftAddons} currentMonthAddOns={currentMonthAddOns} />
    ),
    [draftAddons, currentMonthAddOns]
  )

  const commonProps = {
    onUpdate: handleUpdateAddon,
    isDecreaseDisabled,
    renderChangeStatus,
  }

  return (
    <div>
      {/* Mobile View */}
      <div className="mt-2 space-y-1 rounded-lg border md:hidden">
        {addons.map((addon) => (
          <AddonRow key={addon.id} addon={addon} {...commonProps} />
        ))}
      </div>

      {/* Desktop View */}
      <div className="mt-2 hidden rounded-lg border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-3/5">Feature</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {addons.map((addon) => (
              <AddonTableRow key={addon.id} addon={addon} {...commonProps} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const AddonQuantityControls: FC<{
  addonId: string
  addonName: string
  onUpdate: (addonId: string, delta: number) => void
  isDecreaseDisabled: boolean
  changeStatus: React.ReactNode
}> = ({ addonId, addonName, onUpdate, isDecreaseDisabled, changeStatus }) => (
  <div className="flex flex-col items-center gap-2 md:flex-row md:justify-center">
    <Button
      variant="ghost"
      size="icon"
      onClick={(e) => onUpdate(addonId, e.shiftKey ? -10 : -1)}
      disabled={isDecreaseDisabled}
      aria-label={`Decrease ${addonName} quantity`}
    >
      <MinusCircle className="h-5 w-5" />
    </Button>
    {changeStatus}
    <Button
      variant="ghost"
      size="icon"
      onClick={(e) => onUpdate(addonId, e.shiftKey ? 10 : 1)}
      aria-label={`Increase ${addonName} quantity`}
    >
      <PlusCircle className="h-5 w-5" />
    </Button>
  </div>
)

const AddonChangeStatus: FC<{ addonId: string; draftAddons: PlanAddOn[]; currentMonthAddOns: PlanAddOn[] }> = ({
  addonId,
  draftAddons,
  currentMonthAddOns,
}) => {
  const draftQuantity = draftAddons.find((addon) => addon.productId === addonId)?.quantity || 0
  const currentQuantity = currentMonthAddOns.find((addon) => addon.productId === addonId)?.quantity || 0

  const hasChanges = draftQuantity !== currentQuantity
  const isImmediate = draftQuantity > currentQuantity

  return (
    <div className="w-16 pt-2 text-center md:pt-4">
      <span className="font-medium">{hasChanges ? `${currentQuantity} → ${draftQuantity}` : currentQuantity}</span>
      <div className="h-4 text-[0.7rem]">
        {hasChanges && (
          <span className={isImmediate ? 'text-grass-10' : 'text-amber-11'}>
            {isImmediate ? (
              'Immediate'
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">Next Month</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>The current limit will remain active until the next billing cycle.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </span>
        )}
      </div>
    </div>
  )
}

const AddonPrice: FC<{ price: number; discount?: number }> = ({ price, discount }) => {
  if (!discount) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">${price}</span>
      </div>
    )
  }

  const discountedPrice = Math.max(0, price - discount)
  return (
    <div className="flex items-center gap-2 md:flex-col md:items-baseline md:gap-1">
      <span className="text-sm font-medium">${discountedPrice}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground line-through">${price}</span>
        <div className="flex items-center gap-1 rounded bg-grass-3 px-1.5 py-0.5 text-xs text-grass-11">
          <Tag className="h-3 w-3" />
          <span className="whitespace-nowrap">Save ${discount}</span>
        </div>
      </div>
    </div>
  )
}

const AddonRow: FC<{
  addon: AddonData
  onUpdate: (addonId: string, delta: number) => void
  isDecreaseDisabled: (addonId: string) => boolean
  renderChangeStatus: (addonId: string) => React.ReactNode
}> = ({ addon, onUpdate, isDecreaseDisabled, renderChangeStatus }) => (
  <div className="flex flex-col border-b p-3 last:border-b-0">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="mb-1 flex items-center gap-2">
          <Badge color="blue" variant="secondary" className="h-9 w-9 rounded border-none">
            <ProductIcon quota={addon.quotaName as any} className="h-full w-full" />
          </Badge>
          <p className="font-medium">{addon.name}</p>
        </div>
        <p className="mb-2 text-sm text-muted-foreground">{addon.description}</p>
        <AddonPrice price={addon.price} discount={addon.discount} />
      </div>
      <div className="ml-4 flex flex-col items-center gap-1">
        <AddonQuantityControls
          addonId={addon.id}
          addonName={addon.name}
          onUpdate={onUpdate}
          isDecreaseDisabled={isDecreaseDisabled(addon.id)}
          changeStatus={renderChangeStatus(addon.id)}
        />
      </div>
    </div>
  </div>
)

const AddonTableRow: FC<{
  addon: AddonData
  onUpdate: (addonId: string, delta: number) => void
  isDecreaseDisabled: (addonId: string) => boolean
  renderChangeStatus: (addonId: string) => React.ReactNode
}> = ({ addon, onUpdate, isDecreaseDisabled, renderChangeStatus }) => (
  <TableRow>
    <TableCell>
      <div className="flex items-start gap-3">
        <Badge color="blue" className="h-9 w-9 shrink-0 rounded border-none">
          <ProductIcon quota={addon.quotaName as any} className="h-full w-full" />
        </Badge>
        <div>
          <p className="font-medium">{addon.name}</p>
          <p className="text-sm text-muted-foreground">{addon.description}</p>
        </div>
      </div>
    </TableCell>
    <TableCell>
      <AddonPrice price={addon.price} discount={addon.discount} />
    </TableCell>
    <TableCell className="text-center">
      <AddonQuantityControls
        addonId={addon.id}
        addonName={addon.name}
        onUpdate={onUpdate}
        isDecreaseDisabled={isDecreaseDisabled(addon.id)}
        changeStatus={renderChangeStatus(addon.id)}
      />
    </TableCell>
  </TableRow>
)
